import * as React from 'react';
import { throttle } from 'lodash';
import { findClosestNumber } from '@doltech/utils/lib/number';

export const ScrollDirectionEnum = {
  NOT_SCROLLING: 'NOT_SCROLLING',
  UP: 'UP',
  DOWN: 'DOWN',
} as const;

export type ScrollDirectionEnum = typeof ScrollDirectionEnum[keyof typeof ScrollDirectionEnum];

interface UseScrollTrackingProps {
  containerEl?: HTMLElement;
  elementIds?: string[];
  stickyTop?: number;
  disable?: boolean;
}

export const useScrollTracking = (props?: UseScrollTrackingProps) => {
  const { containerEl: inputContainerEl, elementIds, stickyTop = 0, disable = false } = props;
  const [activeElementIndex, setActiveElementIndex] = React.useState(0);
  const [scrollDirection, setScrollDirection] = React.useState<ScrollDirectionEnum>(ScrollDirectionEnum.NOT_SCROLLING);

  React.useEffect(() => {
    if (disable) {
      return;
    }
    const containerEl = inputContainerEl || document.getElementById('app-root');
    let prevScrollTop = containerEl.scrollTop;

    const handleScroll = throttle((e: any) => {
      const containerElScrollTop = e.target.scrollTop || e.target.scrollingElement?.scrollTop;

      // Detect scroll direction
      if (containerElScrollTop < prevScrollTop) {
        setScrollDirection(ScrollDirectionEnum.UP);
      } else {
        setScrollDirection(ScrollDirectionEnum.DOWN);
      }
      prevScrollTop = e.target.scrollTop;

      // If elementIds available -> detect active element in view
      if (elementIds?.length) {
        const elementOffsetTops = elementIds?.map((eachElementId) => {
          const element = document.getElementById(eachElementId);
          return element?.offsetTop - stickyTop;
        }).filter(Boolean);

        if (elementOffsetTops?.length) {
          const { matchIndex } = findClosestNumber(elementOffsetTops, containerElScrollTop);
          setActiveElementIndex(matchIndex);
        }
      }
    }, 300, { trailing: true, leading: false }); // trailing: true => handle when finish scroll only

    containerEl.addEventListener('scroll', handleScroll, true);

    return () => {
      containerEl.removeEventListener('scroll', handleScroll, true);
    };
  }, [disable, elementIds, inputContainerEl, stickyTop]);

  return {
    activeElementId: elementIds?.[activeElementIndex],
    activeElementIndex,
    scrollDirection,
  };
};
